import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { homeTilesApi } from '../api/home-tiles.api';
import { isHomeTileConflictError, getErrorMessage } from '../utils/errors';
import type { CreateHomeTileDto, UpdateHomeTileDto } from '../types';

export function useHomeTiles() {
  return useQuery({
    queryKey: ['home-tiles'],
    queryFn: () => homeTilesApi.getAll(),
  });
}

export function useCreateHomeTile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateHomeTileDto) => homeTilesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['home-tiles'] });
      notifications.show({
        title: 'Успешно',
        message: 'Плитка успешно создана',
        color: 'green',
      });
    },
    onError: (error) => {
      if (isHomeTileConflictError(error)) {
        notifications.show({
          title: 'Ошибка',
          message: 'Элемент уже добавлен на главную страницу',
          color: 'red',
        });
      } else {
        const errorMessage = getErrorMessage(error);
        notifications.show({
          title: 'Ошибка',
          message: errorMessage,
          color: 'red',
        });
      }
    },
  });
}

export function useUpdateHomeTile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data, file }: { id: number; data: UpdateHomeTileDto; file?: File }) =>
      homeTilesApi.update(id, data, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['home-tiles'] });
    },
  });
}

export function useDeleteHomeTile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => homeTilesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['home-tiles'] });
      notifications.show({
        title: 'Успешно',
        message: 'Плитка успешно удалена',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Ошибка',
        message: 'Ошибка при удалении плитки',
        color: 'red',
      });
    },
  });
}

export function useReorderHomeTiles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (items: { id: number; order: number }[]) => homeTilesApi.updateOrder(items),
    onSuccess: (data) => {
      queryClient.setQueryData(['home-tiles'] as const, data);
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: ['home-tiles'] });
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось изменить порядок плиток',
        color: 'red',
      });
    },
  });
}

